/**
 * Columns of the orders Excel export: the useful order information, in a readable order.
 */
import type { TFunction } from 'i18next';
import moment from 'moment';
import type { OrderDetailsRow } from '../store/api/detailedReportsApi';
import type { CsvColumn } from './csv';

const dateTime = (value: string | null) => (value ? moment(value).format('YYYY-MM-DD HH:mm') : null);

export function orderExportColumns(t: TFunction): CsvColumn<OrderDetailsRow>[] {
  const label = (key: string) => t(`reports.detailed.orderFields.${key}`);
  const status = (v: string) => (v ? t(`components.status.${v}`, { defaultValue: v }) : null);
  const payment = (v: string) => (v ? t(`orders.timeline.payment.${v}`, { defaultValue: v }) : null);
  const gender = (v: string) => (v === 'M' ? t('users.gender.M') : v === 'F' ? t('users.gender.F') : null);

  return [
    { label: label('order_number'), value: (r) => r.order_number },
    { label: label('status'), value: (r) => status(r.status) },
    { label: label('created_at'), value: (r) => dateTime(r.created_at) },
    { label: label('created_source'), value: (r) => (r.created_source ? t(`orders.timeline.source.${r.created_source}`) : null) },
    { label: label('customer_name'), value: (r) => r.customer_name },
    { label: label('customer_phone'), value: (r) => r.customer_phone },
    { label: label('patient_name'), value: (r) => r.patient_name },
    { label: label('patient_age'), value: (r) => r.patient_age },
    { label: label('patient_gender'), value: (r) => gender(r.patient_gender) },
    { label: label('patient_phone'), value: (r) => r.patient_phone },
    { label: label('subservice_name'), value: (r) => [r.subservice_name, r.service_name].filter(Boolean).join(' - ') || null },
    { label: label('provider_name'), value: (r) => r.provider_name },
    {
      label: label('scheduled_date'),
      value: (r) => (r.scheduled_date ? `${r.scheduled_date} ${r.scheduled_time_from ?? ''}-${r.scheduled_time_to ?? ''}`.trim() : null),
    },
    { label: label('full_address'), value: (r) => r.full_address },
    { label: label('total_amount'), value: (r) => r.total_amount },
    { label: label('payment_method'), value: (r) => r.payment_method },
    { label: label('payment_status'), value: (r) => payment(r.payment_status) },
    { label: label('accepted_at'), value: (r) => dateTime(r.accepted_at) },
    { label: label('accepted_by'), value: (r) => r.accepted_by },
    { label: label('completed_at'), value: (r) => dateTime(r.completed_at) },
    { label: label('cancellation_reason'), value: (r) => r.cancellation_reason },
    { label: label('service_rating'), value: (r) => r.service_rating },
    { label: label('provider_rating'), value: (r) => r.provider_rating },
  ];
}
